import { all, put, takeLatest } from "redux-saga/effects";
import {
  componentKey,
  setCloseAddUserProfileDialog,
  setcloseEditTaskDialog,
  setCloseChangePasswordDialog,
  setDepartmentList,
  setproviderTaskListData,
  setUserProfileData,
} from "./providerProfileSlice";
import store from "../../store/store";
import {
  hideLoader,
  showLoader,
} from "../../component-lib/Molecules/loader/LoaderSlice";
import { eventEHRCustomToastMessage } from "../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";
import ProviderProfileService from "../../services/ProviderProfileService";
import AuthDataService from "../../services/AuthDataService";

export const {
  getProviderTaskList,
  updateEditTaskData,
  getUserById,
  updateProfileDetailsData,
  getDepartmentList,
  changePassword,
} = {
  getProviderTaskList: (payload, screen) => {
    const actionType =
      screen === "provider"
        ? "PROVIDER_PROFILE/GET_TASK_LIST"
        : "SETTINGS_SCREEN/GET_TASK_LIST";

    return {
      type: actionType,
      payload,
    };
  },
  updateEditTaskData: (payload, screen) => {
    const actionType =
      screen === "provider"
        ? "PROVIDER_PROFILE/UPDATE_TASK_DATA"
        : "SETTINGS_SCREEN/UPDATE_TASK_DATA";

    return {
      type: actionType,
      payload,
    };
  },
  getUserById: (payload) => {
    const actionType = "PROVIDER_PROFILE/GET_USER_BY_ID";
    return {
      type: actionType,
      payload,
    };
  },
  updateProfileDetailsData: (payload) => {
    return {
      type: "PROVIDER_PROFILE/PATCH_USER_DATA",
      payload,
    };
  },

  getDepartmentList: (payload) => {
    return {
      type: "PROVIDER_PROFILE/GET_DEPARTMENT",
      payload,
    };
  },
  changePassword: (payload) => {
    return {
      type: "PROVIDER_PROFILE/CHANGE_PASSWORD",
      payload,
    };
  },
};

// GET TASK DATA ASYNC
function* getProviderTaskListAsync(action) {
  try {
    yield put(showLoader());
    const response = yield ProviderProfileService.getProviderTaskList(
      action?.payload
    );
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;
      yield put(setproviderTaskListData(data));
    } else {
      console.error("No data returned from API");
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setproviderTaskListData({}));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// UPDATE TASK DATA ASYNC
function* updateEditTaskDataAsync(action) {
  const { apiPayload, taskId } = action?.payload;
  try {
    yield put(showLoader());
    const response = yield ProviderProfileService.updateEditTaskData(
      taskId,
      apiPayload
    );
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;

      eventEHRCustomToastMessage({
        title: `${message}`,
        status: "success",
      });
      yield put(setcloseEditTaskDialog(true));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setcloseEditTaskDialog(false));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// UPDATE TASK DATA ASYNC
function* getUserByIdAsync(action) {
  // const { apiPayload, id } = action?.payload;
  try {
    yield put(showLoader());
    const response = yield ProviderProfileService.getUserById(action?.payload);
    const { status } = response;
    if (status) {
      yield put(setUserProfileData(response?.data));
    } else {
      console.error("No data returned from API");
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setUserProfileData({}));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// PATCH USER PROFILE DATA ASYNC
function* updateProfileDetailsDataAsync(action) {
  const { userId, payload } = action?.payload;
  try {
    yield put(showLoader());
    const response = yield ProviderProfileService.updateProfileDetailsData(
      userId,
      payload
    );
    const { status } = response;
    if (status) {
      const { data, message } = response?.data;

      eventEHRCustomToastMessage({
        title: `${message}`,
        status: "success",
      });
      yield put(setCloseAddUserProfileDialog(true));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setCloseAddUserProfileDialog(false));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// GET DEPARTMENT DATA ASYNC
function* getDepartmentListAsync(action) {
  try {
    yield put(showLoader());
    // Page through ALL departments (mirrors getFacilitiesAsync) so the
    // dropdown is never truncated by a fixed page limit.
    // NOTE: facilityId is intentionally NOT passed here — the request
    // interceptor adds it for every GET. Sending it in the query (where the
    // service lists it first) makes the interceptor's strip/re-add corrupt the
    // URL once other params are present. See providerProfileSaga fix.
    const first = yield ProviderProfileService.getDepartmentList({
      pageNumber: 1,
      limit: 100,
    });
    if (first?.status) {
      let department = first?.data?.department ?? [];
      const totalPages = first?.data?.totalPages ?? 1;
      if (totalPages > 1) {
        const rest = yield Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ProviderProfileService.getDepartmentList({
              pageNumber: i + 2,
              limit: 100,
            }).then((r) => r?.data?.department ?? [])
          )
        );
        department = department.concat(...rest);
      }
      yield put(setDepartmentList({ department }));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    yield put(setDepartmentList({}));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

// CHANGE PASSWORD ASYNC
function* changePasswordAsync(action) {
  try {
    yield put(showLoader());
    const response = yield AuthDataService.changePassword(action?.payload);
    const { status } = response;
    if (status) {
      const { message } = response?.data || {};
      eventEHRCustomToastMessage({
        title: message || "Password changed successfully",
        status: "success",
      });
      yield put(setCloseChangePasswordDialog(true));
    }
  } catch (error) {
    eventEHRCustomToastMessage({
      title: error?.response?.data?.message || "Failed to change password",
      status: "error",
    });
    yield put(setCloseChangePasswordDialog(false));
    console.log("err: ", error);
  } finally {
    yield put(hideLoader());
  }
}

function* rootSaga() {
  yield all([takeLatest(getProviderTaskList().type, getProviderTaskListAsync)]);
  yield all([takeLatest(updateEditTaskData().type, updateEditTaskDataAsync)]);
  yield all([takeLatest(getUserById().type, getUserByIdAsync)]);
  yield all([
    takeLatest(updateProfileDetailsData().type, updateProfileDetailsDataAsync),
  ]);
  yield all([takeLatest(getDepartmentList().type, getDepartmentListAsync)]);
  yield all([takeLatest(changePassword().type, changePasswordAsync)]);
}

store.sagaManager.addSaga(componentKey, rootSaga);
